import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { getDocs, serverTimestamp, doc, setDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { v4 as uuidv4 } from "uuid";
import Nav from "./Nav";
import Sidebar from "./Sidebar";
import TablaOrdenes from "./TablaOrdenes";
import DatePicker, { registerLocale } from "react-datepicker";
import es from "date-fns/locale/es";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { useNavContext } from "../context/NavContext";
import { useNumeroConfig } from "../hooks/useNumeroConfig";
import useArchivos from "../hooks/useArchivos";
import useProductos from "../hooks/useProductos";
import { useAuth } from "../context/AuthContext";
import { getSubCollection } from "../utils/getSubCollection";

moment.locale("es");
registerLocale("es", es);

function OrdenForm(props) {
  const { orden, closeModal, cloneMode = false } = props;
  const { user, teamOwnerId } = useAuth();
  const ownerUid = teamOwnerId || user.uid;
  const [parentId] = useState(orden?.id ?? uuidv4());
  const [productos, setProductos] = useState([]);
  const {
    numero: numeroOrdenAuto,
    modo: modoNumeroOrden,
    abrirConfiguracion,
    NumeroConfigModalUI,
  } = useNumeroConfig("numeroOrden", { title: "Configurar número de orden" });
  const {
    archivos,
    cargando,
    manejarArchivos,
    eliminarArchivo,
    descargarArchivo,
    obtenerIcono,
  } = useArchivos("ordenes", parentId);

  const {
    datos,
    descuento,
    añadirFila,
    eliminar,
    actualizarCantidad,
    handleInputChange,
    filteredProductos,
    activeRow,
    subtotalConDesc,
    iva,
    total,
    manejarCambioDescuento,
    obtenerIVA,
    calcularSubtotal,
    setDatos,
    setFilteredProductos,
    agregarBulk,
    setDescuento,
  } = useProductos(
    orden?.productosSeleccionados || [],
    orden?.descuento || 0,
    productos
  );
  const [numeroSobrescrito, setNumeroSobrescrito] = useState(null);
  const [comprador, setComprador] = useState("");
  const [referencia, setReferencia] = useState("");
  const [incoterm, setInconterm] = useState("");
  const [divisa, setDivisa] = useState("MXN");
  const [condicion, setCondicion] = useState("");
  const [cotizacion, setCotizacion] = useState("");
  const [proveedor, setProveedor] = useState("");
  const [proveedores, setProveedores] = useState([]);
  const [fechaVencimiento, setFechaVencimiento] = useState("");
  const [mostrarTabla] = useState(false);
  const [estado, setEstado] = useState("Pendiente");
  const [ordenes, setOrdenes] = useState([]);
  const [formDirty, setFormDirty] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [fechaOrden, setFechaOrden] = useState(new Date());
  const [notas, setNotas] = useState("");
  const { proximasAVencer, proximosEventos } = useNavContext();
  const [terminos, setTerminos] = useState("");

  useEffect(() => {
    if (orden && !cloneMode) {
      // ── MODO EDICIÓN ─────────────────────────────
      setModoEdicion(true);

      // 1) Productos y descuento
      setDatos(
        (orden.productosSeleccionados || []).map((p) => ({
          id: uuidv4(),
          ...p,
        }))
      );
      setDescuento(parseFloat(orden.descuento) || 0);

      // 2) Archivos (los maneja useArchivos)

      // 3) Resto de campos
      setProveedor(orden.proveedor || "");
      setComprador(orden.comprador || "");
      setReferencia(orden.referencia || "");
      setCotizacion(orden.cotizacion || "");
      setFechaOrden(orden.fechaOrden ? new Date(orden.fechaOrden) : new Date());
      setFechaVencimiento(
        orden.fechaVencimiento ? new Date(orden.fechaVencimiento) : null
      );
      setEstado(orden.estado || "Pendiente");
      setInconterm(orden.incoterm || "");
      setDivisa(orden.divisa || "MXN");
      setCondicion(orden.condicion || "");
      setNotas(orden.notas || "");
      setTerminos(orden.terminos || "");
      setNumeroSobrescrito(orden.numeroOrden ?? null);
    } else if (orden && cloneMode) {
      // ── MODO CLONACIÓN ────────────────────────────
      setModoEdicion(false);

      // Precargamos todos los campos para crear una nueva
      setDatos(
        (orden.productosSeleccionados || []).map((p) => ({
          id: uuidv4(),
          ...p,
        }))
      );
      setDescuento(parseFloat(orden.descuento) || 0);

      setProveedor(orden.proveedor || "");
      setComprador(orden.comprador || "");
      setReferencia(orden.referencia || "");
      setCotizacion(orden.cotizacion || "");
      setFechaOrden(orden.fechaOrden ? new Date(orden.fechaOrden) : new Date());
      setFechaVencimiento(
        orden.fechaVencimiento ? new Date(orden.fechaVencimiento) : null
      );
      setEstado(orden.estado || "Pendiente");
      setInconterm(orden.incoterm || "");
      setDivisa(orden.divisa || "MXN");
      setCondicion(orden.condicion || "");
      setNotas(orden.notas || "");
      setTerminos(orden.terminos || "");
      // Para clonar, mantenemos el número visible pero no lo consideramos edición
      setNumeroSobrescrito(orden.numeroOrden ?? null);
    } else {
      // ── MODO CREACIÓN NUEVA ───────────────────────
      setModoEdicion(false);
      setNumeroSobrescrito(null);

      // Inicializar una fila vacía
      setDatos([
        {
          id: uuidv4(),
          nombre: "",
          numeroDeParteCliente: "",
          cantidad: 1,
          tarifa: 0,
          iva: 0,
          descuento: 0,
          tipoDescuento: "%",
          importe: 0,
        },
      ]);
      setDescuento(0);

      setProveedor("");
      setComprador("");
      setReferencia("");
      setCotizacion("");
      setFechaOrden(new Date());
      setFechaVencimiento(null);
      setEstado("Pendiente");
      setInconterm("");
      setDivisa("MXN");
      setCondicion("");
      setNotas("");
      setTerminos("");
    }
  }, [orden, cloneMode, setDatos, setDescuento]);

  useEffect(() => {
    async function fetchData() {
      try {
        // 1) Referencias a subcolecciones bajo usuarios/{ownerUid}
        const proveedoresCol = getSubCollection(ownerUid, "proveedores");
        const productosCol = getSubCollection(ownerUid, "productosClientes");

        // 2) Disparamos ambas lecturas en paralelo
        const [provSnap, prodSnap] = await Promise.all([
          getDocs(proveedoresCol),
          getDocs(productosCol),
        ]);

        // 3) Formateamos y ordenamos proveedores
        const proveedoresList = provSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) =>
            (a.empresa || "").localeCompare(b.empresa || "", "es", {
              sensitivity: "base",
            })
          )
          .filter(
            (prov, idx, arr) =>
              arr.findIndex((x) => x.empresa === prov.empresa) === idx
          );

        setProveedores(proveedoresList);

        // 4) Formateamos productos de clientes
        const productosList = prodSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          tipoDescuento: "cantidad",
          valorDescuento: 0,
        }));
        setProductos(productosList);
      } catch (error) {
        console.error("Error al cargar proveedores o productos:", error);
      }
    }

    fetchData();
  }, [ownerUid]);

  const handleCancel = () => {
    if (formDirty) {
      Swal.fire({
        title: "¿Seguro que quieres salir?",
        text: "Se perderán los cambios realizados",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, salir",
        cancelButtonText: "Cancelar",
      }).then((result) => {
        if (result.isConfirmed) {
          // Cerrar el modal usando la función pasada como prop
          closeModal();
          setFormDirty(false); // Opcional: resetea el estado si es necesario
        }
      });
    } else {
      // Si no hay cambios, simplemente cierra el modal
      closeModal();
    }
  };

  const guardar = async (e) => {
    e.preventDefault();

    /* 1) VALIDACIÓN */
    if (
      !proveedor ||
      !(numeroSobrescrito ?? numeroOrdenAuto) ||
      !fechaOrden ||
      !comprador
    ) {
      return Swal.fire({
        icon: "error",
        title: "Campos obligatorios faltantes",
        text: "Por favor completa: Proveedor, Número de Orden, Fecha y Comprador.",
      });
    }

    const numeroEfectivo = numeroSobrescrito ?? numeroOrdenAuto;

    try {
      /* 2) DUPLICADOS */
      const ordenesCol = getSubCollection(ownerUid, "ordenes");
      const snap = await getDocs(ordenesCol);
      const dup = snap.docs.find(
        (d) => d.data().numeroOrden === numeroEfectivo
      );
      if (dup && (!modoEdicion || dup.id !== orden?.id)) {
        return Swal.fire({
          icon: "error",
          title: "Número de orden duplicado",
          text: `El número "${numeroEfectivo}" ya existe. Usa otro.`,
        });
      }

      /* 3) PAYLOAD saneado */
      const selProv = proveedores.find((p) => p.id === proveedor) || {};
      const archivosURLs = (archivos || []).map(({ nombre, url }) => ({
        nombre: nombre || "",
        url: url || "",
      }));

      // Normalizar productos
      const productosNorm = (datos || []).map((p) => ({
        id: p.id || uuidv4(),
        nombre: p.nombre || "",
        modelo: p.modelo || p.numeroDeParteCliente || "",
        cantidad: Number(p.cantidad) || 0,
        tarifa: Number(p.tarifa) || 0,
        iva: Number(p.iva) || 0,
        descuento: Number(p.descuento) || 0,
        tipoDescuento: p.tipoDescuento || "%",
        importe: Number(p.importe) || 0,
        descripcion: p.descripcion || "",
      }));

      const ordenData = {
        ownerId: ownerUid,
        proveedor: proveedor || "",
        nombreProveedor: selProv.empresa || "",
        numeroOrden: numeroEfectivo,
        comprador: comprador || "",
        referencia: referencia || "",
        fechaOrden:
          fechaOrden instanceof Date ? fechaOrden.toISOString() : null,
        fechaVencimiento:
          fechaVencimiento instanceof Date
            ? fechaVencimiento.toISOString()
            : null,
        estado: estado || "Pendiente",
        incoterm: incoterm || "",
        divisa: divisa || "MXN",
        condicion: condicion || "",
        cotizacion: cotizacion || "",
        notas: notas || "",
        terminos: terminos || "",
        productosSeleccionados: productosNorm,
        total: Number(total) || 0,
        descuento: Number(descuento) || 0,
        archivos: archivosURLs,
        ...(modoEdicion
          ? { updatedAt: serverTimestamp() }
          : { createdAt: serverTimestamp() }),
      };

      /* 4) CREACIÓN O EDICIÓN */
      if (modoEdicion && orden?.id) {
        const ref = doc(ordenesCol, orden.id);
        await setDoc(ref, ordenData, { merge: true });

        setOrdenes?.((prev) =>
          prev.map((o) =>
            o.id === orden.id ? { id: orden.id, ...ordenData } : o
          )
        );

        await Swal.fire({
          icon: "success",
          title: "Orden actualizada",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        const ref = doc(ordenesCol, parentId);
        await setDoc(ref, ordenData);

        if (modoNumeroOrden === "auto") {
          const m = String(numeroEfectivo).match(/^(.+?)(\d+)$/);
          if (m) {
            const [, pref, num] = m;
            const siguienteNumero = Number(num) + 1;
            const configRef = doc(
              db,
              "usuarios",
              ownerUid,
              "configuracion",
              "numeroOrden"
            );
            await setDoc(
              configRef,
              { modo: "auto", prefijo: pref, siguienteNumero },
              { merge: true }
            );
          }
        }

        await Swal.fire({
          icon: "success",
          title: "Orden guardada",
          timer: 1500,
          showConfirmButton: false,
        });
      }

      /* 5) LIMPIEZA Y CIERRE */
      setFormDirty(false);
      closeModal();
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Error al guardar la orden",
        text: err.message,
      });
    }
  };

  return (
    <>
      <Nav
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
      />

      <Sidebar />

      <div className="cotizacion-form">
        <form
          onKeyDown={(e) => {
            // Solo bloquear "Enter" fuera de los elementos <textarea>
            if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
              e.preventDefault();
            }
          }}
        >
          <div>
            <div className="group apartado">
              <label htmlFor="proveedor" className="required">
                Proveedor*
              </label>
              <select
                id="proveedor"
                name="proveedor"
                value={proveedor}
                onChange={(e) => {
                  setProveedor(e.target.value);
                  setFormDirty(true); // Marcar que el formulario ha sido modificado
                }}
              >
                <option value="">Seleccionar Proveedor</option>
                {proveedores.map((proveedor) => (
                  <option key={proveedor.id} value={proveedor.id}>
                    {proveedor.empresa}
                  </option>
                ))}
              </select>
            </div>

            <br />

            <div className="group">
              <label htmlFor="numeroOrden" className="required">
                No. Orden#*
              </label>
              <div className="input-wrapper">
                <input
                  type="text"
                  id="numeroOrden"
                  name="numeroOrden"
                  // Si el usuario sobreescribe manualmente, usamos ese valor,
                  // si no, el automático que viene del hook.
                  value={numeroSobrescrito ?? numeroOrdenAuto}
                  onChange={(e) => {
                    if (modoNumeroOrden === "manual") {
                      const v = e.target.value.trim();
                      // Si dejan en blanco, volvemos a null para caer en auto
                      setNumeroSobrescrito(v === "" ? null : v);
                    }
                  }}
                  // Deshabilitado en modo automático
                  disabled={modoNumeroOrden === "auto"}
                />
                <button
                  type="button"
                  className="icon-button"
                  // Abrimos el modal de configuración proveniente del hook
                  onClick={abrirConfiguracion}
                >
                  <i className="bi bi-gear-fill"></i>
                </button>
              </div>
            </div>

            <div className="group">
              <label htmlFor="comprador" className="required">
                Comprador#*
              </label>
              <input
                type="text"
                id="comprador"
                name="comprador"
                value={comprador}
                onChange={(e) => {
                  setComprador(e.target.value);
                  setFormDirty(true); // Marcar que el formulario ha sido modificado
                }}
              />
            </div>

            <div className="group fechas">
              <div className="group">
                <label htmlFor="fecha-orden" className="required">
                  Fecha de orden*
                </label>
                <DatePicker
                  selected={fechaOrden}
                  onChange={(date) => {
                    setFechaOrden(date);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                  dateFormat="dd MMM yyyy"
                  placeholderText="dd MMM yyyy"
                  id="fecha-cotizacion"
                  locale="es"
                />
              </div>
              <div className="group">
                <label htmlFor="fecha-vencimiento">Fecha de vencimiento</label>
                <div>
                  <DatePicker
                    selected={fechaVencimiento}
                    onChange={(date) => {
                      setFechaVencimiento(date);
                      setFormDirty(true); // Marcar el formulario como modificado
                    }}
                    dateFormat="dd MMM yyyy"
                    placeholderText="dd MMM yyyy"
                    id="fecha-vencimiento"
                    locale="es"
                  />
                </div>
              </div>
            </div>

            <div className="group">
              <label htmlFor="referencia">Referencia:</label>
              <input
                type="text"
                id="referencia"
                name="referencia"
                value={referencia}
                onChange={(e) => {
                  setReferencia(e.target.value);
                  setFormDirty(true); // Marcar que el formulario ha sido modificado
                }}
              />
            </div>

            <hr
              style={{
                marginBottom: "30px",
                marginTop: "20px",
                border: "1px solid #f9f9fb",
              }}
            />

            <div className="group fechas">
              <div className="group">
                <label htmlFor="incoterm">Incoterms:</label>
                <select
                  id="incoterm"
                  name="incoterm"
                  value={incoterm}
                  onChange={(e) => setInconterm(e.target.value)}
                  style={{ width: "42%" }}
                >
                  <option value="ninguno">Ninguno</option>
                  <option value="exw">EXW</option>
                  <option value="fob">FOB</option>
                  <option value="cif">CIF</option>
                  <option value="dap">DAP</option>
                  <option value="ddp">DDP</option>
                </select>
              </div>

              <div className="group">
                <label htmlFor="divisa">Divisa</label>
                <select
                  id="divisa"
                  name="divisa"
                  value={divisa}
                  onChange={(e) => {
                    setDivisa(e.target.value);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                  style={{ width: "41%" }}
                >
                  <option value="MXN">MXN</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>

            <div className="group fechas">
              <div className="group">
                <label htmlFor="condicion">Condición de Pago:</label>
                <input
                  type="text"
                  id="condicion"
                  name="condicion"
                  value={condicion}
                  onChange={(e) => setCondicion(e.target.value)}
                />
              </div>

              <div className="group">
                <label htmlFor="cotizacion">Cotización:</label>
                <input
                  style={{ width: "39%" }}
                  type="text"
                  id="cotizacion"
                  name="cotizacion"
                  value={cotizacion}
                  onChange={(e) => setCotizacion(e.target.value)}
                />
              </div>
            </div>

            <br />

            <div className="apartado2">Tabla de productos</div>

            <div>
              {/* Contenedor para la tabla y los botones */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  width: "100%",
                }}
              >
                {/* Tabla general */}

                <div>
                  <table className="productos-table">
                    <thead>
                      <tr>
                        <th>Detalles del Artículo</th>
                        <th style={{ textAlign: "center" }}>Modelo</th>
                        <th id="cantidad-col">Cantidad</th>
                        <th id="precio-col">Precio</th>
                        <th id="descuento-col">Descuento</th>
                        <th id="importe-col">Importe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datos.map((producto) => (
                        <tr key={producto.id} style={{ position: "relative" }}>
                          <td>
                            <div style={{ position: "relative" }}>
                              <textarea
                                className="list-products"
                                value={
                                  producto.descripcion || producto.nombre || ""
                                }
                                onChange={(e) => {
                                  handleInputChange(e, producto.id);
                                  setFormDirty(true);
                                }}
                                onBlur={() =>
                                  setTimeout(
                                    () => setFilteredProductos([]),
                                    100
                                  )
                                }
                                placeholder="Escriba para seleccionar un artículo"
                                style={{
                                  width: "600px",
                                  height: "130px",
                                  padding: "8px",
                                  fontSize: "14px",
                                  lineHeight: "1.5",
                                  resize: "vertical",
                                  overflow: "auto",
                                  whiteSpace: "pre-wrap",
                                  fontFamily: "Arial, sans-serif",
                                }}
                              />
                              {activeRow === producto.id &&
                                filteredProductos.length > 0 && (
                                  <ul
                                    style={{
                                      position: "absolute",
                                      top: "105%",
                                      left: 0,
                                      width: "100%",
                                      backgroundColor: "#fff",
                                      border: "1px solid #cecece",
                                      borderRadius: "5px",
                                      zIndex: 10,
                                      maxHeight: "150px",
                                      overflowY: "auto",
                                      listStyle: "none",
                                      padding: "5px",
                                      margin: 0,
                                    }}
                                  >
                                    {filteredProductos.map((prod) => (
                                      <li
                                        key={prod.id}
                                        onMouseDown={() => {
                                          handleInputChange(
                                            { target: { value: prod.nombre } },
                                            producto.id
                                          );
                                          setFormDirty(true);
                                        }}
                                        style={{
                                          padding: "5px",
                                          cursor: "pointer",
                                          backgroundColor: "#f9f9f9",
                                          marginBottom: "2px",
                                        }}
                                      >
                                        {prod.nombre}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                            </div>
                          </td>

                          <td>
                            <input
                              type="text"
                              value={
                                producto.modelo ||
                                producto.numeroDeParteCliente ||
                                ""
                              }
                              onChange={(e) => {
                                const modelo = e.target.value;
                                setDatos((prev) =>
                                  prev.map((item) =>
                                    item.id === producto.id
                                      ? { ...item, modelo }
                                      : item
                                  )
                                );
                                setFormDirty(true);
                              }}
                            />
                          </td>

                          <td id="cantidad-col">
                            <input
                              type="number"
                              value={producto.cantidad || 1}
                              onChange={(e) => {
                                actualizarCantidad(
                                  producto.id,
                                  parseInt(e.target.value) || 0
                                );
                                setFormDirty(true);
                              }}
                            />
                          </td>

                          <td id="precio-col">
                            <input
                              type="text"
                              value={
                                producto.tarifa !== undefined &&
                                producto.tarifa !== ""
                                  ? producto.tarifa.toString()
                                  : ""
                              }
                              onFocus={(e) => {
                                e.target.value =
                                  parseFloat(producto.tarifa) || "";
                              }}
                              onChange={(e) => {
                                const raw = e.target.value.replace(
                                  /[^0-9.]/g,
                                  ""
                                );
                                if (/^\d*\.?\d*$/.test(raw)) {
                                  const tarifa = parseFloat(raw) || 0;
                                  setDatos((prev) =>
                                    prev.map((item) =>
                                      item.id === producto.id
                                        ? {
                                            ...item,
                                            tarifa,
                                            importe: tarifa * item.cantidad,
                                          }
                                        : item
                                    )
                                  );
                                  setFormDirty(true);
                                }
                              }}
                              onBlur={(e) => {
                                const num = parseFloat(e.target.value);
                                if (!isNaN(num)) {
                                  setDatos((prev) =>
                                    prev.map((item) =>
                                      item.id === producto.id
                                        ? {
                                            ...item,
                                            tarifa: num,
                                            importe: num * item.cantidad,
                                          }
                                        : item
                                    )
                                  );
                                  e.target.value = num.toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  });
                                }
                              }}
                              style={{
                                width: "100%",
                                textAlign: "right",
                                padding: "8px",
                                fontSize: "14px",
                                borderRadius: "4px",
                              }}
                            />
                          </td>

                          <td id="descuento-col">
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                height: "32px",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                overflow: "hidden",
                                width: "100%",
                              }}
                            >
                              {/* Input de descuento */}
                              <input
                                type="text"
                                min="0"
                                step="1"
                                value={producto.descuento || 0}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (/^\d*\.?\d*$/.test(v)) {
                                    let d = parseFloat(v) || 0;
                                    if (
                                      producto.tipoDescuento === "%" &&
                                      d > 100
                                    )
                                      d = 100;
                                    setDatos((prev) =>
                                      prev.map((item) =>
                                        item.id === producto.id
                                          ? {
                                              ...item,
                                              descuento: d,
                                              importe:
                                                item.tipoDescuento === "%"
                                                  ? item.tarifa *
                                                    item.cantidad *
                                                    (1 - d / 100)
                                                  : item.tarifa *
                                                      item.cantidad -
                                                    d,
                                            }
                                          : item
                                      )
                                    );
                                    setFormDirty(true);
                                  }
                                }}
                                style={{
                                  flex: 1,
                                  border: "none",
                                  outline: "none",
                                  textAlign: "right",
                                  padding: "0 12px",
                                  fontSize: "16px",
                                  lineHeight: "32px",
                                }}
                              />

                              {/* Select de tipo con icono */}
                              <div
                                style={{
                                  position: "relative",
                                  width: "50px",
                                  height: "100%",
                                }}
                              >
                                <select
                                  value={producto.tipoDescuento || "%"}
                                  onChange={(e) => {
                                    const tipo = e.target.value;
                                    setDatos((prev) =>
                                      prev.map((item) =>
                                        item.id === producto.id
                                          ? {
                                              ...item,
                                              tipoDescuento: tipo,
                                              importe:
                                                tipo === "%"
                                                  ? item.tarifa *
                                                    item.cantidad *
                                                    (1 - item.descuento / 100)
                                                  : item.tarifa *
                                                      item.cantidad -
                                                    item.descuento,
                                            }
                                          : item
                                      )
                                    );
                                    setFormDirty(true);
                                  }}
                                  style={{
                                    appearance: "none",
                                    WebkitAppearance: "none",
                                    MozAppearance: "none",
                                    width: "100%",
                                    height: "100%",
                                    border: "none",
                                    outline: "none",
                                    padding: "0 12px",
                                    fontSize: "16px",
                                    lineHeight: "32px",
                                    backgroundColor: "#f4f4f4",
                                    textAlign: "center",
                                    cursor: "pointer",
                                  }}
                                >
                                  <option value="%">%</option>
                                  <option value="$">$</option>
                                </select>
                                <i
                                  style={{
                                    position: "absolute",
                                    right: "8px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    pointerEvents: "none",
                                    color: "#666",
                                    fontSize: "12px",
                                  }}
                                />
                              </div>
                            </div>
                          </td>

                          <td
                            id="importe-col"
                            style={{ textAlign: "right", paddingRight: "10px" }}
                          >
                            $
                            {parseFloat(producto.importe || 0).toLocaleString(
                              "en-US",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}
                          </td>

                          {/* Botones de acción */}
                          <div
                            style={{
                              position: "absolute",
                              right: "-75px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              display: "flex",
                            }}
                          >
                            <button
                              type="button"
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className="bi bi-three-dots-vertical"
                                style={{ fontSize: "1.2rem" }}
                              />
                            </button>
                            <button
                              type="button"
                              style={{
                                background: "none",
                                border: "none",
                                color: "#dc3545",
                                cursor: "pointer",
                              }}
                              onClick={() => eliminar(producto.id)}
                            >
                              <i
                                className="bi bi-x-lg"
                                style={{ fontSize: "1.2rem" }}
                              />
                            </button>
                          </div>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="acciones-tabla">
              <button onClick={añadirFila}>
                <i
                  style={{ color: "#007bff", fontSize: "16px" }}
                  className="bi bi-plus-circle-fill"
                ></i>
                Añadir nueva fila
              </button>
              <button type="button" onClick={() => agregarBulk(datos)}>
                <i
                  style={{ color: "#007bff", fontSize: "16px" }}
                  className="bi bi-plus-circle-fill"
                ></i>
                Agregar artículos a granel
              </button>
            </div>

            <div className="data-container">
              <div className="notes-container">
                <label htmlFor="customer-notes">Notas del Proveedor</label>
                <textarea
                  id="customer-notes"
                  placeholder="Escribe tus notas aquí..."
                  value={notas}
                  onChange={(e) => {
                    setNotas(e.target.value);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                ></textarea>
              </div>

              <div className="totales-container">
                {/* Subtotal después de aplicar el descuento */}
                <div className="row">
                  <span className="label">Subtotal</span>
                  <span>
                    $
                    {subtotalConDesc.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>

                {/* Descuento en porcentaje */}
                <div className="row descuento">
                  <span className="label">Descuento</span>
                  <div className="descuento-input-container">
                    <input
                      type="text"
                      className="descuento-input"
                      value={descuento}
                      onChange={manejarCambioDescuento}
                    />
                    <span className="percentage-sign">%</span>
                  </div>
                  <span className="discount-percentage">
                    – $
                    {(calcularSubtotal() * (descuento / 100)).toLocaleString(
                      "en-US",
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }
                    )}
                  </span>
                </div>

                {/* IVA calculado */}
                <div className="row iva">
                  <span className="label">
                    IVA ({(obtenerIVA() * 100).toFixed(0)}%)
                  </span>
                  <span>
                    $
                    {iva.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>

                <hr />

                {/* Total final */}
                <div className="row total">
                  <span>Total ({divisa})</span>
                  <span className="total-amount">
                    $
                    {total.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </div>
            </div>

            <div
              style={{
                backgroundColor: "#f9f9fb",
                padding: "8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "10px",
                width: "94%",
                marginTop: "30px",
                paddingBottom: "30px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              }}
            >
              {/* Left Section: Términos y Condiciones */}
              <div className="terminos-container">
                <h4>Términos y condiciones</h4>
                <textarea
                  value={terminos}
                  onChange={(e) => {
                    setTerminos(e.target.value);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                />
              </div>

              {/* Right Section: File Upload */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "8px",
                  flex: "0 0 530px",
                  marginTop: "30px",
                }}
              >
                <label style={{ fontSize: "14px" }}>
                  Adjuntar archivo(s) a Estimación
                </label>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    border: "1px dashed #ccc",
                    borderRadius: "5px",
                    overflow: "hidden", // Eliminar cualquier contenido que se salga del borde
                  }}
                >
                  <label
                    style={{
                      padding: "8px 10px",
                      backgroundColor: "#fff",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      whiteSpace: "nowrap", // Evitar que el contenido del botón se ajuste en varias líneas
                    }}
                  >
                    <i style={{ color: "#000" }} className="bi bi-upload"></i>
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#000",
                        fontWeight: "lighter",
                      }}
                    >
                      Cargar archivo
                    </span>
                    <input
                      type="file"
                      style={{ display: "none" }} // Ocultar el input de archivo
                      accept=".csv,.xls,.xlsx,.doc,.docx,.pdf,.jpg,.jpeg,.png,.svg,.txt"
                      onChange={manejarArchivos}
                    />
                  </label>

                  <div
                    style={{
                      padding: "10px",
                      backgroundColor: "#fff",
                      borderLeft: "1px dashed #ccc", // Borde que conecta con el input
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      borderTopRightRadius: "auto", // Bordes redondeados en la esquina derecha
                      borderBottomRightRadius: "auto",
                      minWidth: "20px", // Evitar que el botón se haga demasiado pequeño
                      margin: "0", // Eliminar cualquier margen que agregue espacio extra
                    }}
                  >
                    <i
                      style={{
                        color: "#000",
                        fontSize: "14px", // Ajustar el tamaño del ícono
                      }}
                      className="bi bi-chevron-down"
                    ></i>
                  </div>
                </div>

                <p style={{ fontSize: "12px", color: "#888" }}>
                  Puedes cargar un máximo de 10 archivos, cada uno de 10 MB
                </p>

                {cargando && (
                  <p style={{ fontSize: "14px", color: "#888" }}>
                    Cargando archivo...
                  </p>
                )}

                {archivos.length > 0 && (
                  <div style={{ width: "100%" }}>
                    <strong>Archivos cargados:</strong>
                    <ul
                      style={{
                        listStyle: "none",
                        padding: 0,
                        marginTop: "10px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      {archivos.map(({ nombre, url }) => {
                        const ext = nombre.split(".").pop().toLowerCase();
                        const { icon, color } = obtenerIcono(ext);

                        return (
                          <li
                            key={nombre}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                              padding: "10px",
                              border: "1px solid #ddd",
                              borderRadius: "5px",
                              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                              backgroundColor: "#f9f9fb",
                            }}
                          >
                            <i
                              className={`bi ${icon}`}
                              style={{
                                color,
                                fontSize: "1.5rem",
                                flexShrink: 0,
                              }}
                            />
                            <span style={{ flexGrow: 1 }}>{nombre}</span>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                descargarArchivo(url, nombre);
                              }}
                              style={{
                                color: "#007bff",
                                background: "none",
                                border: "none",
                                fontWeight: "bold",
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className="bi bi-download"
                                style={{ fontSize: "20px" }}
                              />
                            </button>
                            <button
                              onClick={() => eliminarArchivo(nombre)}
                              style={{
                                border: "none",
                                background: "transparent",
                                color: "#dc3545",
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className="bi bi-trash"
                                style={{ fontSize: "20px" }}
                              />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Grupo de botones: Guardar como borrador, Guardar y enviar, Cancelar */}
          <div className="cotizacion-toolbar">
            <div className="btn-group">
              <button
                id="guardar-enviar"
                className="btn btn-primary"
                type="button"
                onClick={guardar}
              >
                {modoEdicion ? "Actualizar" : "Guardar y enviar"}
              </button>
              <button
                id="cancelar"
                className="btn btn-secondary"
                type="button"
                onClick={handleCancel}
              >
                Cancelar
              </button>
            </div>
          </div>
        </form>

        {mostrarTabla && (
          <TablaOrdenes ordenes={ordenes} setOrdenes={setOrdenes} />
        )}
      </div>

      <NumeroConfigModalUI />
    </>
  );
}

export default OrdenForm;
